import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { PaymentMethodName } from 'data/__generated__';

// Assez pour voir d'un coup d'œil que deux lignes partagent le même identifiant ; l'identifiant complet est au survol.
// Par la FIN : un PaymentIntent Stripe commence par un horodatage et l'identifiant du compte, deux batchs du même soir
// partagent leurs onze premiers caractères.
const ABBREVIATED_LENGTH = 8;

export const paymentPath = (paymentId: string): string => `/payment/${paymentId}`;
// Le `_id` d'un `TransactionPayment` côté API : la position de l'entrée dans `transaction.paymentMethods`.
export const transactionPaymentPath = (transactionId: string, paymentIndex: number): string =>
    paymentPath(`${transactionId}-${paymentIndex}`);
export const paymentMethodPath = (paymentMethodId: string): string => `/payment-method/${paymentMethodId}`;
export const creditPath = (creditId: string): string => `/credit/${creditId}`;

// Les intégrations (Nayax, Vendon, Odoo, Sensei…) posent un `_id` constant : il ne désigne aucun instrument, et le
// suivre listerait tous leurs paiements.
export function getPaymentInstrumentId({
    _id,
    paymentMethodName,
}: {
    _id: string;
    paymentMethodName: PaymentMethodName;
}): string | null {
    return paymentMethodName === PaymentMethodName.ThirdPartyPaymentMethod ? null : _id;
}

// Chaque type de paiement désigne un document d'une autre collection, qui a sa propre fiche. Le badge pro n'en a pas :
// sa fiche est celle de l'organisation, que l'entrée de paiement ne porte pas.
const PATH_BY_PAYMENT_METHOD_NAME: Record<PaymentMethodName, ((id: string) => string) | null> = {
    [PaymentMethodName.CreditCardPaymentMethod]: paymentMethodPath,
    [PaymentMethodName.MealVoucherPaymentMethod]: paymentMethodPath,
    [PaymentMethodName.CreditPaymentMethod]: creditPath,
    [PaymentMethodName.SubventionPaymentMethod]: (id) => `/subvention/${id}`,
    [PaymentMethodName.RitualPaymentMethod]: (id) => `/ritual/${id}`,
    // L'entrée d'une formule porte l'identifiant de sa DÉFINITION, pas celui de la formule achetée.
    [PaymentMethodName.MealPlanPaymentMethod]: (id) => `/meal-plan-definition/${id}`,
    [PaymentMethodName.OrganizationCardPaymentMethod]: null,
    [PaymentMethodName.ThirdPartyPaymentMethod]: null,
};

export function getPaymentMethodTargetPath({
    _id,
    paymentMethodName,
}: {
    _id: string;
    paymentMethodName: PaymentMethodName;
}): string | null {
    return PATH_BY_PAYMENT_METHOD_NAME[paymentMethodName]?.(_id) ?? null;
}

// `to` ouvre une fiche (celle du moyen de paiement), `onSelect` filtre sur place la table qui affiche l'identifiant, et
// sans l'un ni l'autre le lien ouvre la page Paiements filtrée sur lui.
export const PaymentIdLink = ({
    id,
    abbreviated = true,
    to,
    onSelect,
}: {
    id?: string | null;
    abbreviated?: boolean;
    to?: string;
    onSelect?: (id: string) => void;
}) => {
    if (!id) {
        return <span>-</span>;
    }

    const label = abbreviated && id.length > ABBREVIATED_LENGTH ? `…${id.slice(-ABBREVIATED_LENGTH)}` : id;

    if (!to && onSelect) {
        return (
            <IdButton type="button" title={id} onClick={() => onSelect(id)}>
                {label}
            </IdButton>
        );
    }

    return (
        <IdLink to={to ?? `/payments?${new URLSearchParams({ searchValue: id })}`} title={id}>
            {label}
        </IdLink>
    );
};

const idStyle = css`
    color: ${({ theme }) => theme.ctaPrimaryHoveredColor};
    font-family: monospace;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
        text-decoration: underline;
    }
`;

const IdLink = styled(Link)`
    ${idStyle}
`;

const IdButton = styled.button`
    ${idStyle}
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
`;
